import React, {Component} from 'react';
import {Link} from 'react-router-dom';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {LayoutSidebar} from '../../../Layout';
import {Spinner} from '../../Spinner';
import {FaIcon} from '../../FaIcon';
import {Switch} from '../../buttons/Swtich';
import {LocalStorage} from '../../../utils/LocalStorage';
import {YesNoModal} from '../../elements/YesNoModal';
import {TemplateSectionCard} from './TemplateSectionCard';
import {BlockEditorModal} from './BlockEditorModal';
import {withTranslation} from 'react-i18next';
import {withRouterParams} from '../../../utils/withRouterParams';
import './TemplateEditorPage.scss';


const CHANNEL_LABELS = {
    GOOGLE_CHANNEL: 'Google',
    FACEBOOK_CHANNEL: 'Meta',
    TIKTOK_CHANNEL: 'TikTok',
    LINKEDIN_CHANNEL: 'LinkedIn',
    ANALYTICS_CHANNEL: 'GA4',
    SHEET_CHANNEL: 'Sheets',
    SHOPIFY_CHANNEL: 'Shopify',
    TIENDA_NUBE_CHANNEL: 'Tiendanube',
    WOOCOMMERCE_CHANNEL: 'WooCommerce',
    VTEX_CHANNEL: 'VTEX',
};

function formatDate(dateStr, language) {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString(language === 'es' ? 'es-AR' : 'en-US', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    } catch {
        return dateStr;
    }
}


class TemplateEditorPageClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            template: null,
            originalTemplate: null,
            loading: true,
            saving: false,
            hasChanges: false,
            editingBlock: null,
            editingBlockSectionIndex: null,
            editingBlockIndex: null,
            showBlockEditor: false,
            showDeleteConfirm: false,
            deleting: false,
            errors: {},
            saveMessage: null,
        };

        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
        this.handleSharedChange = this.handleSharedChange.bind(this);
        this.handleExtraBlocksChange = this.handleExtraBlocksChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleDiscard = this.handleDiscard.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleMoveSectionUp = this.handleMoveSectionUp.bind(this);
        this.handleMoveSectionDown = this.handleMoveSectionDown.bind(this);
        this.handleDeleteSection = this.handleDeleteSection.bind(this);
        this.handleEditBlock = this.handleEditBlock.bind(this);
        this.handleAddBlock = this.handleAddBlock.bind(this);
        this.handleBlockSave = this.handleBlockSave.bind(this);
        this.handleBlockDelete = this.handleBlockDelete.bind(this);
        this.toggleBlockEditor = this.toggleBlockEditor.bind(this);
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    }

    componentDidMount() {
        this.fetchTemplate();
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    handleBeforeUnload(e) {
        if (this.state.hasChanges) {
            e.preventDefault();
            e.returnValue = '';
        }
    }

    fetchTemplate() {
        const templateId = this.props.match.params.id;
        this.setState({loading: true});
        new APIClient(API_URL + 'reports/templates/user/' + templateId + '/').call('GET').then((data) => {
            if (data && data.status === 'OK') {
                const template = data.data;
                this.setState({
                    template: JSON.parse(JSON.stringify(template)),
                    originalTemplate: JSON.parse(JSON.stringify(template)),
                    loading: false,
                });
            } else {
                this.props.navigate('/reports/templates');
            }
        });
    }

    checkChanges(template) {
        const hasChanges = JSON.stringify(template) !== JSON.stringify(this.state.originalTemplate);
        this.setState({hasChanges});
    }

    updateTemplate(updater) {
        this.setState(prev => {
            const template = JSON.parse(JSON.stringify(prev.template));
            updater(template);
            return {template, hasChanges: JSON.stringify(template) !== JSON.stringify(prev.originalTemplate)};
        });
    }

    handleNameChange(e) {
        this.updateTemplate(t => { t.name = e.target.value; });
    }

    handleDescriptionChange(e) {
        this.updateTemplate(t => { t.description = e.target.value; });
    }

    handleSharedChange(e) {
        this.updateTemplate(t => { t.is_shared = e.target.checked; });
    }

    handleExtraBlocksChange(e) {
        this.updateTemplate(t => {
            if (!t.ai_settings) t.ai_settings = {};
            t.ai_settings.default_allow_extra_blocks = e.target.checked;
        });
    }

    handleSave() {
        const {template, originalTemplate} = this.state;
        if (!template) return;

        // Validaciones
        if (!template.name || !template.name.trim()) {
            this.setState({errors: {name: true}});
            return;
        }

        this.setState({saving: true, errors: {}, saveMessage: null});

        // Construir diff payload
        const payload = {};
        if (template.name !== originalTemplate.name) payload.name = template.name;
        if (template.description !== originalTemplate.description) payload.description = template.description;
        if (template.is_shared !== originalTemplate.is_shared) payload.is_shared = template.is_shared;
        if (JSON.stringify(template.ai_settings) !== JSON.stringify(originalTemplate.ai_settings)) {
            payload.ai_settings = template.ai_settings;
        }
        if (JSON.stringify(template.sections) !== JSON.stringify(originalTemplate.sections)) {
            payload.sections = template.sections;
        }

        const templateId = this.props.match.params.id;
        new APIClient(API_URL + 'reports/templates/user/' + templateId + '/').call('PATCH', payload).then((data) => {
            if (data && data.status === 'OK') {
                const updated = data.data;
                this.setState({
                    template: JSON.parse(JSON.stringify(updated)),
                    originalTemplate: JSON.parse(JSON.stringify(updated)),
                    saving: false,
                    hasChanges: false,
                    saveMessage: 'success',
                });
                setTimeout(() => this.setState({saveMessage: null}), 3000);
            } else if (data && data.status === 'NOT_FOUND') {
                this.props.navigate('/reports/templates');
            } else {
                this.setState({
                    saving: false,
                    saveMessage: 'error',
                    errors: data?.errors || {},
                });
            }
        });
    }

    handleDiscard() {
        this.setState(prev => ({
            template: JSON.parse(JSON.stringify(prev.originalTemplate)),
            hasChanges: false,
            errors: {},
            saveMessage: null,
        }));
    }

    handleDelete() {
        const templateId = this.props.match.params.id;
        this.setState({deleting: true});
        new APIClient(API_URL + 'reports/templates/user/' + templateId + '/').call('DELETE').then((data) => {
            this.setState({deleting: false, showDeleteConfirm: false});
            if (data && data.status === 'OK') {
                this.props.navigate('/reports/templates');
            }
        });
    }

    handleMoveSectionUp(index) {
        if (index === 0) return;
        this.updateTemplate(t => {
            const sections = t.sections;
            [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
        });
    }

    handleMoveSectionDown(index) {
        this.updateTemplate(t => {
            const sections = t.sections;
            if (index >= sections.length - 1) return;
            [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
        });
    }

    handleDeleteSection(index) {
        const {t} = this.props;
        if (!window.confirm(t('templates.section_delete_confirm'))) return;
        this.updateTemplate(tmpl => {
            tmpl.sections.splice(index, 1);
        });
    }

    handleEditBlock(sectionIndex, blockIndex, block) {
        this.setState({
            editingBlock: block,
            editingBlockSectionIndex: sectionIndex,
            editingBlockIndex: blockIndex,
            showBlockEditor: true,
        });
    }

    handleAddBlock(sectionIndex) {
        this.setState({
            editingBlock: null,
            editingBlockSectionIndex: sectionIndex,
            editingBlockIndex: null,
            showBlockEditor: true,
        });
    }

    toggleBlockEditor() {
        this.setState({showBlockEditor: false, editingBlock: null});
    }

    handleBlockSave(blockData) {
        const {editingBlockSectionIndex, editingBlockIndex} = this.state;
        this.updateTemplate(t => {
            const section = t.sections[editingBlockSectionIndex];
            if (!section.blocks) section.blocks = [];
            if (editingBlockIndex !== null && editingBlockIndex !== undefined) {
                // Editing existing block
                section.blocks[editingBlockIndex] = blockData;
            } else {
                // Adding new block - generate ID
                const existingIds = section.blocks.map(b => b.id).filter(Boolean);
                let counter = existingIds.length + 1;
                let newId = `${section.id}_${blockData.type}_${counter}`;
                while (existingIds.includes(newId)) {
                    counter++;
                    newId = `${section.id}_${blockData.type}_${counter}`;
                }
                blockData.id = newId;
                section.blocks.push(blockData);
            }
        });
        this.setState({showBlockEditor: false, editingBlock: null});
    }

    handleBlockDelete() {
        const {editingBlockSectionIndex, editingBlockIndex} = this.state;
        if (editingBlockIndex === null || editingBlockIndex === undefined) return;
        this.updateTemplate(t => {
            t.sections[editingBlockSectionIndex].blocks.splice(editingBlockIndex, 1);
        });
        this.setState({showBlockEditor: false, editingBlock: null});
    }

    render() {
        const {t} = this.props;
        const {template, loading, saving, hasChanges, showDeleteConfirm, deleting, errors, saveMessage, showBlockEditor, editingBlock} = this.state;
        const language = this.props.i18n.language;

        if (loading) {
            return (
                <div className="app-content">
                    <LayoutSidebar/>
                    <section className="section-content">
                        <div className="template-editor-loading">
                            <Spinner color="positive"/>
                        </div>
                    </section>
                </div>
            );
        }

        if (!template) return null;

        const currentUserId = LocalStorage.fetchUserId();
        const isOwner = template.created_by && template.created_by.id === currentUserId;
        const sections = template.sections || [];
        const totalBlocks = sections.reduce((acc, s) => acc + (s.blocks?.length || 0), 0);

        return (
            <div className="app-content">
                <LayoutSidebar/>
                <section className="section-content">
                    <header className="section-header">
                        <div className="template-editor-header-top">
                            <Link to="/reports/templates" className="template-editor-back">
                                <FaIcon iconName="arrow-left"/>
                                {t('templates.editor_back')}
                            </Link>
                        </div>
                        <div className="template-editor-header-main">
                            <h1 className="section-title">{template.name}</h1>
                            <div className="template-editor-header-badges">
                                {template.required_channels && template.required_channels.map(ch => (
                                    <span key={ch} className="template-editor-channel-badge">
                                        {CHANNEL_LABELS[ch] || ch}
                                    </span>
                                ))}
                                {template.is_shared && (
                                    <span className="template-editor-shared-badge">
                                        <FaIcon iconName="users"/>
                                        {t('templates.badge_shared')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </header>

                    <div className="section-body">
                        {!isOwner && (
                            <div className="template-editor-readonly-banner">
                                <FaIcon iconName="eye"/>
                                {t('templates.readonly_notice', {name: template.created_by?.name || ''})}
                            </div>
                        )}

                        {/* Información General */}
                        <div className="box template-editor-info-box">
                            <div className="box-header">
                                <h3 className="box-title">{t('templates.editor_info_title')}</h3>
                            </div>
                            <div className="template-editor-info-content">
                                <div className="form-row">
                                    <label className="template-editor-field-label">
                                        {t('templates.editor_name_label')}
                                    </label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.name ? '-error' : ''}`}
                                        value={template.name}
                                        onChange={this.handleNameChange}
                                        maxLength={255}
                                        disabled={!isOwner}
                                    />
                                </div>
                                <div className="form-row">
                                    <label className="template-editor-field-label">
                                        {t('templates.editor_description_label')}
                                    </label>
                                    <textarea
                                        className="form-control"
                                        value={template.description || ''}
                                        onChange={this.handleDescriptionChange}
                                        rows={3}
                                        disabled={!isOwner}
                                    />
                                </div>
                                <div className="template-editor-toggle-row">
                                    <div className="template-editor-toggle-text">
                                        <span className="template-editor-toggle-label">{t('templates.editor_share_label')}</span>
                                        <span className="template-editor-toggle-hint">{t('templates.editor_share_hint')}</span>
                                    </div>
                                    <Switch
                                        name="isShared"
                                        checked={template.is_shared || false}
                                        onChange={this.handleSharedChange}
                                        style={{margin: 0}}
                                        disabled={!isOwner}
                                    />
                                </div>
                                <div className="template-editor-toggle-row">
                                    <div className="template-editor-toggle-text">
                                        <span className="template-editor-toggle-label">{t('templates.editor_extra_blocks_label')}</span>
                                        <span className="template-editor-toggle-hint">{t('templates.editor_extra_blocks_hint')}</span>
                                    </div>
                                    <Switch
                                        name="allowExtraBlocks"
                                        checked={template.ai_settings?.default_allow_extra_blocks || false}
                                        onChange={this.handleExtraBlocksChange}
                                        style={{margin: 0}}
                                        disabled={!isOwner}
                                    />
                                </div>
                                <div className="template-editor-readonly-info">
                                    {template.source_template_name && (
                                        <span>
                                            {t('templates.editor_origin')}: {template.source_template_name}
                                        </span>
                                    )}
                                    <span>
                                        {t('templates.editor_created')}: {formatDate(template.created_on, language)}
                                        {template.created_by && ` · ${t('templates.by_author', {name: template.created_by.name})}`}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Estructura del Template */}
                        <div className="box template-editor-structure-box">
                            <div className="box-header">
                                <h3 className="box-title">{t('templates.structure_title')}</h3>
                                <span className="template-editor-sections-count">
                                    {t('templates.sections_count', {sections: sections.length, blocks: totalBlocks})}
                                </span>
                            </div>
                            <div className="template-editor-sections">
                                {sections.map((section, index) => (
                                    <TemplateSectionCard
                                        key={section.id || index}
                                        section={section}
                                        sectionIndex={index}
                                        totalSections={sections.length}
                                        t={t}
                                        isOwner={isOwner}
                                        onMoveUp={this.handleMoveSectionUp}
                                        onMoveDown={this.handleMoveSectionDown}
                                        onDelete={this.handleDeleteSection}
                                        onEditBlock={this.handleEditBlock}
                                        onAddBlock={this.handleAddBlock}
                                    />
                                ))}
                                {sections.length === 0 && (
                                    <div className="template-editor-no-sections">
                                        <FaIcon iconName="circle-info"/>
                                        <span>{t('templates.empty_subtitle')}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Zona de peligro */}
                        {isOwner && (
                            <div className="template-editor-danger-zone">
                                <YesNoModal
                                    isOpen={showDeleteConfirm}
                                    title={t('templates.delete_template')}
                                    question={t('templates.delete_confirm')}
                                    onYesAnswer={this.handleDelete}
                                    onNoAnswer={() => this.setState({showDeleteConfirm: false})}
                                    isSaving={deleting}
                                    buttonText={t('templates.delete_template')}
                                    buttonIconName="trash"
                                    buttonClassName="btn -negative -danger"
                                    skipRenderButton={true}
                                />
                                <button
                                    className="btn -negative -danger template-editor-delete-btn"
                                    onClick={() => this.setState({showDeleteConfirm: true})}
                                >
                                    <FaIcon iconName="trash"/>
                                    {t('templates.delete_template')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Save bar */}
                    {hasChanges && isOwner && (
                        <div className="template-editor-save-bar">
                            {saveMessage === 'error' && (
                                <span className="template-editor-save-error">
                                    <FaIcon iconName="circle-exclamation"/>
                                    {t('templates.save_error')}
                                </span>
                            )}
                            <button className="btn -negative" onClick={this.handleDiscard} disabled={saving}>
                                {t('templates.discard_changes')}
                            </button>
                            <button className="btn -alt" onClick={this.handleSave} disabled={saving}>
                                {saving ? (
                                    <><Spinner color="light" size="sm"/> {t('templates.saving')}</>
                                ) : (
                                    t('templates.save_changes')
                                )}
                            </button>
                        </div>
                    )}

                    {saveMessage === 'success' && (
                        <div className="template-editor-save-toast">
                            <FaIcon iconName="circle-check"/>
                            {t('templates.save_success')}
                        </div>
                    )}
                </section>

                <BlockEditorModal
                    isOpen={showBlockEditor}
                    toggle={this.toggleBlockEditor}
                    block={editingBlock}
                    onSave={this.handleBlockSave}
                    onDelete={this.handleBlockDelete}
                />
            </div>
        );
    }
}

export const TemplateEditorPage = withTranslation()(withRouterParams(TemplateEditorPageClass));
